/**
 * @fileoverview Memory Card Component
 * @description Flippable card with animation for the memory game
 */

import React, { useEffect, useRef } from "react";
import {
  TouchableOpacity,
  View,
  Image,
  StyleSheet,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { MemoryCardProps } from "../types";
import { getCardImageUrl } from "../hooks/useMemoryGame";

/**
 * MemoryCard - A flippable memory game card
 */
export const MemoryCard: React.FC<MemoryCardProps> = ({
  card,
  isRevealed,
  isMatched,
  disabled,
  onPress,
  cardSize,
}) => {
  const flipAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Flip animation
  useEffect(() => {
    Animated.spring(flipAnim, {
      toValue: isRevealed || isMatched ? 1 : 0,
      friction: 8,
      tension: 100,
      useNativeDriver: true,
    }).start();
  }, [isRevealed, isMatched, flipAnim]);

  // Match celebration animation
  useEffect(() => {
    if (isMatched) {
      Animated.sequence([
        Animated.timing(scaleAnim, {
          toValue: 1.1,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [isMatched, scaleAnim]);

  const frontInterpolate = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: ["180deg", "90deg", "0deg"],
  });

  const backInterpolate = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: ["0deg", "90deg", "180deg"],
  });

  const frontOpacity = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  const backOpacity = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0, 0],
  });

  /**
   * Handle card press
   */
  const handlePress = () => {
    if (!disabled && !isMatched && !isRevealed) {
      onPress();
    }
  };

  /**
   * Get image source
   */
  const getImageSource = () => {
    // Check if it's a URL (family photo from Supabase) or local asset
    if (card.imageUrl.startsWith("http")) {
      return { uri: card.imageUrl };
    }
    // Local asset - require dynamically based on filename
    const imagePath = getCardImageUrl(card.imageUrl, "random");
    // For local images, we need to use require with a static path
    // We'll use a mapping approach
    return getLocalImage(card.imageUrl);
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={disabled || isMatched || isRevealed}
      activeOpacity={0.8}
      accessibilityLabel={`Kaart ${card.position + 1}${
        isMatched ? ", gevonden" : isRevealed ? ", zichtbaar" : ", verborgen"
      }`}
      accessibilityHint={
        disabled || isMatched || isRevealed
          ? undefined
          : "Dubbeltik om kaart om te draaien"
      }
      accessibilityRole="button"
      style={[
        styles.container,
        {
          width: cardSize,
          height: cardSize,
        },
      ]}
    >
      <Animated.View
        style={[
          styles.cardContainer,
          {
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        {/* Back of card (pattern/logo) */}
        <Animated.View
          style={[
            styles.card,
            styles.cardBack,
            {
              transform: [{ rotateY: backInterpolate }],
              opacity: backOpacity,
            },
          ]}
        >
          <View style={styles.backPattern}>
            <Ionicons name="help" size={32} color="#7C3AED" />
          </View>
        </Animated.View>

        {/* Front of card (image) */}
        <Animated.View
          style={[
            styles.card,
            styles.cardFront,
            isMatched && styles.matchedCard,
            {
              transform: [{ rotateY: frontInterpolate }],
              opacity: frontOpacity,
            },
          ]}
        >
          <Image
            source={getImageSource()}
            style={styles.cardImage}
            resizeMode="cover"
          />
          {isMatched && (
            <View style={styles.matchedOverlay}>
              <Ionicons name="checkmark-circle" size={28} color="#22C55E" />
            </View>
          )}
        </Animated.View>
      </Animated.View>
    </TouchableOpacity>
  );
};

/**
 * Get local image based on filename
 * We use a switch/mapping because require() needs static strings
 */
const getLocalImage = (filename: string) => {
  switch (filename) {
    case "airplace.jpg":
      return require("@/assets/memory_game_images/airplace.jpg");
    case "friendly_blue_monster.jpg":
      return require("@/assets/memory_game_images/friendly_blue_monster.jpg");
    case "funny_monster.jpg":
      return require("@/assets/memory_game_images/funny_monster.jpg");
    case "minion_hero.jpg":
      return require("@/assets/memory_game_images/minion_hero.jpg");
    case "minion_muscle.jpg":
      return require("@/assets/memory_game_images/minion_muscle.jpg");
    case "purple_monther.jpg":
      return require("@/assets/memory_game_images/purple_monther.jpg");
    case "r2d2.jpg":
      return require("@/assets/memory_game_images/r2d2.jpg");
    case "robot.jpg":
      return require("@/assets/memory_game_images/robot.jpg");
    case "spce.jpg":
      return require("@/assets/memory_game_images/spce.jpg");
    case "spiderman.jpg":
      return require("@/assets/memory_game_images/spiderman.jpg");
    case "sweet_monster.jpg":
      return require("@/assets/memory_game_images/sweet_monster.jpg");
    case "toystory.jpg":
      return require("@/assets/memory_game_images/toystory.jpg");
    case "yellow_pumpkin.jpg":
      return require("@/assets/memory_game_images/yellow_pumpkin.jpg");
    case "yellow.jpg":
      return require("@/assets/memory_game_images/yellow.jpg");
    default:
      return require("@/assets/memory_game_images/robot.jpg");
  }
};

const styles = StyleSheet.create({
  container: {
    margin: 4,
  },
  cardContainer: {
    flex: 1,
    position: "relative",
  },
  card: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    backfaceVisibility: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  cardBack: {
    backgroundColor: "#1E293B",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#7C3AED",
  },
  backPattern: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(124, 58, 237, 0.1)",
    borderRadius: 10,
  },
  cardFront: {
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  matchedCard: {
    borderWidth: 3,
    borderColor: "#22C55E",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    borderRadius: 10,
  },
  matchedOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
  },
});

export default MemoryCard;

